/**
 * Connections — one page for every external connection.
 *
 * Aug 2026 redesign (UX review): each connector renders as a compact row —
 * [name + ring chip] [live sensor] [green intent toggle] — grouped by product
 * line, with a troubleshoot body that auto-expands when the sensor needs
 * attention. Enabling a connector fires a live test immediately so the sensor
 * resolves in seconds instead of waiting for the hourly health cycle.
 *
 * Deep-link contract: `window.openSettings('connections', { connector })`
 * (or a `settings:navigate` event carrying `detail.connector`) lands here as
 * the `focusConnector` prop — the row scrolls into view, expands, and
 * flashes. This is the target of every "fix it on Settings → Connections"
 * pointer (health alerts, triage-dashboard hot links).
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plug, RefreshCw, PlayCircle, Loader2, AlertTriangle,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { confirmDialog } from '../ConfirmDialog';
import ConnectorRow, { needsAttention, timeAgo } from './ConnectorRow';
import GoogleAccounts from './GoogleAccounts';
import InboundAssistants from './InboundAssistants';
import './Connections.css';

const HEALTH_LABELS = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  expired: 'Expired — reconnect',
  failed: 'Failed',
  disabled: 'Disabled',
  unknown: 'Not checked',
};

const LINE_LABELS = {
  intel: 'Intelligence (INTEL)',
  flow: 'Action & Tasks (FLOW)',
  context: 'Account Context (CONTEXT)',
  platform: 'Platform',
};

function HealthPill({ health }) {
  return (
    <span className={`cx-pill cx-pill-${health || 'unknown'}`}>
      {HEALTH_LABELS[health] || health}
    </span>
  );
}

export default function Connections({ focusConnector = null }) {
  const queryClient = useQueryClient();
  const [testingKey, setTestingKey] = React.useState(null);
  const [savingKey, setSavingKey] = React.useState(null);
  const [keyError, setKeyError] = React.useState(null); // { key, message }
  // Manual open/close per row; absent → the row follows needsAttention().
  const [expandOverride, setExpandOverride] = React.useState({});
  const [flashKey, setFlashKey] = React.useState(null);
  const rowRefs = React.useRef(new Map());

  const [searchParams] = useSearchParams();
  // OAuth callbacks land back here with ?connector_error= on failure.
  const connectorError = searchParams.get('connector_error');

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['connectors'],
    queryFn: () => apiClient.get('/api/connectors'),
  });

  // "Live monitoring" = the hourly health cycle (proactive OAuth refresh,
  // transition alerts, auto-disable). The flag used to hide on the Platform
  // features page, so the sensors on THIS page silently never self-updated —
  // now the switch lives next to the sensors it powers.
  const { data: integ } = useQuery({
    queryKey: ['integration-settings'],
    queryFn: () => apiClient.get('/api/integration/settings'),
  });
  const liveMonitoring = integ?.settings?.connector_health_enabled ?? false;

  const toggleMonitoring = useMutation({
    mutationFn: (enabled) =>
      apiClient.put('/api/integration/settings', { settings: { connector_health_enabled: enabled } }),
    onMutate: async (enabled) => {
      const previous = queryClient.getQueryData(['integration-settings']);
      queryClient.setQueryData(['integration-settings'], (old) => old && {
        ...old,
        settings: { ...old.settings, connector_health_enabled: enabled },
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => queryClient.setQueryData(['integration-settings'], ctx.previous),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['integration-settings'] }),
  });

  const test = useMutation({
    mutationFn: (key) => apiClient.post(`/api/connectors/${key}/test`, {}),
    onMutate: (key) => setTestingKey(key),
    onSettled: () => {
      setTestingKey(null);
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });

  const testAll = useMutation({
    mutationFn: () => apiClient.post('/api/connectors/test-all', {}),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['connectors'] }),
  });

  const connect = useMutation({
    // Browser OAuth: fetch the authorize URL (JWT-authenticated), then
    // navigate the whole window — the provider redirects back through the
    // state-verified backend callback and returns to Settings. `grant`
    // requests an incremental scope set on the same household.
    mutationFn: ({ key, grant }) =>
      apiClient.get(`/api/connectors/${key}/oauth/start${grant ? `?grant=${encodeURIComponent(grant)}` : ''}`),
    onSuccess: (r) => {
      if (r?.url) window.location.href = r.url;
    },
  });

  const saveKey = useMutation({
    // The generalized BYOK contract: the key is live-validated upstream
    // before it is stored; a bad key comes back as a 400 with the upstream
    // detail and nothing changes.
    mutationFn: ({ key, value }) =>
      apiClient.post(`/api/connectors/${key}/credentials`, { credentials: { api_key: value } }),
    onMutate: ({ key }) => { setSavingKey(key); setKeyError(null); },
    onSuccess: (_r, { onDone }) => onDone?.(),
    onError: (err, { key }) => setKeyError({ key, message: err.message || 'Key validation failed' }),
    onSettled: () => {
      setSavingKey(null);
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
  });

  const disconnect = useMutation({
    mutationFn: (key) => apiClient.delete(`/api/connectors/${key}/credentials`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['connectors'] }),
  });

  const handleDisconnect = async (conn) => {
    const ok = await confirmDialog({
      title: 'Disconnect',
      message: `Disconnect ${conn.displayName}? Its stored credentials are deleted; the row will read "Not connected" until you reconnect.`,
      confirmLabel: 'Disconnect',
    });
    if (ok) disconnect.mutate(conn.key);
  };

  // Google Calendar folded in from the retired standalone settings page —
  // manual sync stays available on its row.
  const syncCalendar = useMutation({
    mutationFn: () => apiClient.post('/api/calendar/sync', {}),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['connectors'] }),
  });
  const extraActionsFor = (key) => (
    key === 'google_calendar'
      ? [{
          label: syncCalendar.isPending ? 'Syncing…' : 'Sync now',
          pending: syncCalendar.isPending,
          onClick: () => syncCalendar.mutate(),
        }]
      : []
  );

  const toggle = useMutation({
    mutationFn: ({ key, enabled }) =>
      apiClient.post(`/api/connectors/${key}/${enabled ? 'enable' : 'disable'}`, {}),
    onMutate: async ({ key, enabled }) => {
      const previous = queryClient.getQueryData(['connectors']);
      queryClient.setQueryData(['connectors'], (old) => old && {
        ...old,
        connectors: old.connectors.map((c) =>
          c.key === key ? { ...c, enabled, health: enabled ? 'unknown' : 'disabled' } : c
        ),
      });
      return { previous };
    },
    onSuccess: (_r, { key, enabled }) => {
      // Flipping ON immediately answers "did that fix it?" — without this the
      // sensor would sit grey until the hourly health cycle comes around.
      if (enabled) test.mutate(key);
    },
    onError: (_err, _vars, ctx) => queryClient.setQueryData(['connectors'], ctx.previous),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['connectors'] }),
  });

  // Deep-link focus: expand, scroll to, and flash the requested row.
  const focusReady = Boolean(
    focusConnector && data?.connectors?.some((c) => c.key === focusConnector)
  );
  React.useEffect(() => {
    if (!focusReady) return undefined;
    setExpandOverride((prev) => ({ ...prev, [focusConnector]: true }));
    setFlashKey(focusConnector);
    // Next frame so the expanded body has laid out before we scroll.
    const raf = requestAnimationFrame(() => {
      rowRefs.current.get(focusConnector)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = setTimeout(() => setFlashKey(null), 3200);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
  }, [focusConnector, focusReady]);

  if (isLoading) {
    return (
      <div className="cx-loading">
        <Loader2 size={32} className="cx-spin" />
        <span>Loading connections…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cx-error cx-error-page">
        <AlertTriangle size={16} /> {error.message}
      </div>
    );
  }

  // Neither loading nor error nor data: a state react-query can land in
  // off a stale persisted-cache hydration. Without this branch the page
  // rendered an empty container (phone-pass report) — say so instead.
  if (!data) {
    return (
      <div className="cx-error cx-error-page">
        <AlertTriangle size={16} /> No response from the connector registry —
        <button className="cx-btn" onClick={() => refetch()} style={{ marginLeft: 8 }}>
          Try again
        </button>
      </div>
    );
  }

  const connectors = data?.connectors || [];
  const feeds = data?.feeds || [];
  const byLine = connectors.reduce((acc, c) => {
    (acc[c.line] = acc[c.line] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="cx-container">
      <div className="cx-header">
        <div className="cx-header-left">
          <Plug size={24} strokeWidth={2} style={{ color: 'var(--color-success, #10b981)' }} />
          <div>
            <h1>Connections</h1>
            <p className="cx-subtitle">
              Toggle is intent; the sensor is truth — steady green means the last
              live check passed
            </p>
          </div>
        </div>
        <div className="cx-header-actions">
          <label
            className="cx-monitor"
            title="Hourly live checks: proactive OAuth refresh, alerts on health transitions, auto-pause on runaway failures"
          >
            <span className="cx-monitor-label">Live monitoring</span>
            <span className="cxr-switch">
              <input
                type="checkbox"
                role="switch"
                checked={liveMonitoring}
                aria-checked={liveMonitoring}
                aria-label="Live monitoring (hourly health checks)"
                onChange={() => toggleMonitoring.mutate(!liveMonitoring)}
              />
              <span className="cxr-slider" aria-hidden="true" />
            </span>
          </label>
          <button
            className="cx-btn"
            onClick={() => testAll.mutate()}
            disabled={testAll.isPending}
          >
            {testAll.isPending ? <Loader2 size={14} className="cx-spin" /> : <PlayCircle size={14} />}
            Test all
          </button>
          <button className="cx-btn cx-btn-ghost" onClick={() => refetch()} title="Refresh">
            <RefreshCw size={14} className={isFetching ? 'cx-spin' : ''} />
          </button>
        </div>
      </div>

      {connectorError && (
        <div className="cx-error cx-error-page">
          <AlertTriangle size={14} /> Connect failed: {connectorError}
        </div>
      )}

      {connect.isError && (
        <div className="cx-error cx-error-page">
          <AlertTriangle size={14} /> Connect failed: {connect.error?.message || 'unknown error'}
        </div>
      )}

      {Object.entries(LINE_LABELS).map(([line, label]) => (
        byLine[line]?.length ? (
          <section key={line} className="cx-section">
            <h2 className="cx-section-title">{label}</h2>
            <div className="cx-rows">
              {byLine[line].map((c) => (
                <ConnectorRow
                  key={c.key}
                  connector={c}
                  expanded={expandOverride[c.key] ?? needsAttention(c)}
                  flash={flashKey === c.key}
                  rowRef={(el) => {
                    if (el) rowRefs.current.set(c.key, el);
                    else rowRefs.current.delete(c.key);
                  }}
                  onToggleExpand={() => setExpandOverride((prev) => ({
                    ...prev,
                    [c.key]: !(prev[c.key] ?? needsAttention(c)),
                  }))}
                  onToggle={(conn) => toggle.mutate({ key: conn.key, enabled: !conn.enabled })}
                  onTest={(k) => test.mutate(k)}
                  testing={testingKey === c.key || (testAll.isPending && c.enabled)}
                  onDisconnect={handleDisconnect}
                  disconnecting={disconnect.isPending && disconnect.variables === c.key}
                  extraActions={extraActionsFor(c.key)}
                  onConnect={(k) => connect.mutate({ key: k })}
                  onGrant={(k, grant) => connect.mutate({ key: k, grant })}
                  // Labeled per-user Google accounts (decision #20) — the
                  // multi-identity chips live on the google row's body.
                  bodyExtra={c.key === 'google' ? <GoogleAccounts /> : null}
                  onSaveKey={(k, value, onDone) => saveKey.mutate({ key: k, value, onDone })}
                  saving={savingKey === c.key}
                  keyError={keyError?.key === c.key ? keyError.message : null}
                />
              ))}
            </div>
          </section>
        ) : null
      ))}

      <InboundAssistants />

      {feeds.length > 0 && (
        <section className="cx-section">
          <h2 className="cx-section-title">Intelligence Feeds</h2>
          <p className="cx-section-note">
            Managed in the feed admin; shown here so one page covers everything.
          </p>
          <div className="cx-feed-table">
            {feeds.map((f) => (
              <div key={f.name} className="cx-feed-row">
                <span className="cx-feed-name">{f.name}</span>
                <span className="cx-feed-type">{f.type}</span>
                <HealthPill health={f.health} />
                <span className="cx-feed-last">Last success: {timeAgo(f.lastSuccess) || 'never'}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
